import { useState, useCallback, useRef } from 'react';
import { API } from "aws-amplify";
import { DynamicSlugPersonQueryTemplate } from "../../graphql/slugqueries";
import { AndEnumFilterGeneratorWithSearch } from "../../graphql/util/filterbuilder";

/**
 * Custom hook for searching people with characteristics and filters
 * @param {Function} setFilteredPeople - Function to update filtered people list
 * @param {Function} setMatchSource - Function to update match source
 * @returns {Object} Search functions and state
 */
export function useSearchPeople(setFilteredPeople, setMatchSource) {
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Use ref instead of module-level variable to prevent shared state between hook instances
  const peopleFromAPIRef = useRef([]);

  const searchPeople = useCallback(async (map, searchString) => {
    if (map === undefined) {
      return;
    }

    // Check if map only contains Dataset or is empty
    const hasOnlyDataset = map.size === 1 && map.has('Dataset');
    const hasNoCharacteristics = map.size === 0 || hasOnlyDataset;

    // Allow search if we have a search string, otherwise prevent
    if (hasNoCharacteristics && (!searchString || searchString.trim() === '')) {
      setSnackbarMessage('Please select at least one characteristic or enter a search term');
      setSnackbarOpen(true);
      setFilteredPeople(new Array(0));
      setMatchSource('manual_filter');
      return;
    }

    const filter2 = AndEnumFilterGeneratorWithSearch(map, searchString);
    const queryTemplate = DynamicSlugPersonQueryTemplate;
    let queryToExecute = "";

    if (map.size > 0) {
      queryToExecute = queryTemplate.replace("FILTER", filter2);

      try {
        const apiData = await API.graphql({
          query: queryToExecute,
          authMode: 'API_KEY'
        });
        peopleFromAPIRef.current = await apiData.data.listSlugPeople.items;

        if (peopleFromAPIRef.current.length > 50) {
          setConfirmOpen(true);
        } else {
          setFilteredPeople(peopleFromAPIRef.current);
          setMatchSource('manual_filter');
        }
      } catch (error) {
        setFilteredPeople(new Array(0));
        setMatchSource('manual_filter');
      }
    } else {
      setFilteredPeople(new Array(0));
      setMatchSource('manual_filter');
    }
  }, [setFilteredPeople, setMatchSource]);

  const pullEntireSet = useCallback(() => {
    setFilteredPeople(peopleFromAPIRef.current);
    setMatchSource('manual_filter');
  }, [setFilteredPeople, setMatchSource]);

  const clearResults = useCallback((defaultMap, setEnumMap) => {
    setFilteredPeople(new Array(0));
    setMatchSource('manual_filter');
    setEnumMap(defaultMap);
  }, [setFilteredPeople, setMatchSource]);

  return {
    searchPeople,
    pullEntireSet,
    clearResults,
    peopleFromAPI: peopleFromAPIRef.current,
    snackbarMessage,
    snackbarOpen,
    setSnackbarOpen,
    confirmOpen,
    setConfirmOpen
  };
}
